import moment from "moment";
import QueryString from "qs";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router-dom";
import ReactToPrint from "react-to-print";
import styled from "styled-components";
import { getAwards } from "../../api/award.api";
import ErrorResponse from "../../components/ErrorResponse";
import NewWindowLayout from "../../components/layout/NewWindowLayout";
import Loader from "../../components/Loader";
import useAxios from "../../hooks/useAxios";
import { IAwardItem, IGetAwards } from "../../models/award.model";
import { RootState } from "../../store/rootReducer";

const PrintContainer = styled.div`
  @font-face {
    font-family: 'Aramis Italic';
    src: url('/fonts/aramis.italic.ttf') format('truetype');
  }
  @import url('https://fonts.googleapis.com/css2?family=Nanum+Myeongjo:wght@700&family=Secular+One&display=swap');

  text-align: center;

  .background {
    font-size: 30px;
    width: 1582px;
    height: 1117px;
    background: url(/images/award.png);
    background-size: contain;
    background-repeat: no-repeat;
  }

  .title {
    position: relative;
    top: 270px;
    font-family: 'Secular One', sans-serif;
    font-size: 42px;
    font-weight: bold;
  }

  .name {
    position: relative;
    top: 400px;
    font-family: 'Aramis Italic';
    font-size: 81px;
  }

  .description {
    position: relative;
    top: 430px;
    display: flex;
    justify-content: center;

    .desc-text {
      line-height: 1.3;
      width: 800px;
      text-align: center;  
    }
  }

  .date {
    position: relative;
    top: 480px;
  }

  .desc-text, .date {
    font-family: 'Nanum Myeongjo', serif;
    font-size: 27px;
    font-weight: 700;
  }
`;

const PrintAward = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const awardCode = String(query.awardCode);
  const memberEngName = String(query.memberEngName);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);  
  const { data: res, isLoading, error, execute } = useAxios<IGetAwards>();
  const printRef = useRef();

  useEffect(() => {
    execute(getAwards());
  }, []);

  const getAwardTitle = (data: IAwardItem[]|undefined, awardCode: string) => {
    const award = data?.find(item => item.awardCode === awardCode);
    return award?.name?.toUpperCase();
  }

  const getAwardDescription = (data: IAwardItem[]|undefined, awardCode: string) => {
    const award = data?.find(item => item.awardCode === awardCode);
    return award?.memo;
  }

  return (
    <NewWindowLayout title={t('class.printAward.title1')} isBoxOff>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />
      <div className="mt-1 ml-1">
        <ReactToPrint
          trigger={() => (
            <button
              className="btn btn-primary"
              style={{ fontSize: '1.5em' }}
              // @ts-ignore
              pageStyle="@page { size: landscape; }"
            >
              {t('class.printAward.button1')}
            </button>
          )}
          // @ts-ignore
          content={() => printRef.current}
          bodyClass="print"
        />
        <button
          className="btn btn-secondary ml-1"
          style={{ fontSize: '1.5em' }}
          onClick={() => window.close()}
        >
          {t('class.printAward.button2')}
        </button>
      </div>
      {/* @ts-ignore */}
      <PrintContainer ref={printRef}>
        <style type="text/css" media="print">
          {' @page { size: landscape; } '}
        </style>
        <div className="background">
          <div className="title">
            OF {getAwardTitle(res?.data, awardCode)} AWARD
          </div>
          <div className="name">
            {memberEngName}
          </div>
          <div className="description">
            <div
              className="desc-text"
              dangerouslySetInnerHTML={{ __html: getAwardDescription(res?.data, awardCode) || '' }}
            />
          </div>
          <div className="date">
            Awarded in {moment(serverDate).locale('en').format('MMMM YYYY')}
          </div>
        </div>
      </PrintContainer>
    </NewWindowLayout>
  );
}

export default PrintAward;
